// src/pages/DiscountCodesPage.js
// หน้าจัดการโค้ดส่วนลด / แคมเปญ (Admin/Manager)

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../AuthContext";
import { useNotification } from "../NotificationContext";
import api, {
  listDiscountCodes,
  createDiscountCode,
  updateDiscountCode,
  deleteDiscountCode
} from "../api";
import SkeletonLoader from "../components/SkeletonLoader";

const DiscountCodesPage = () => {
  const { user } = useContext(AuthContext);
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";

  // Only admin and manager can access
  useEffect(() => {
    if (!isAdmin && !isManager) {
      addNotification("คุณไม่มีสิทธิ์เข้าถึงหน้านี้", "danger");
      navigate("/dashboard");
    }
  }, [isAdmin, isManager, navigate, addNotification]);

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    percent_off: "",
    amount_off: "",
    max_uses: "",
    per_customer_limit: "",
    is_single_use: false,  // ✅ เพิ่ม field
    start_date: "",
    end_date: "",
    is_active: true,
  });

  // Filter & search
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // all | active | inactive

  const loadCodes = async () => {
    setLoading(true);
    try {
      const res = await listDiscountCodes();
      setCodes(res.data || []);
    } catch (err) {
      addNotification("โหลดโค้ดส่วนลดไม่สำเร็จ", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, []);

  const openCreateModal = () => {
    setEditingCode(null);
    setFormData({
      code: "",
      description: "",
      percent_off: "",
      amount_off: "",
      max_uses: "",
      per_customer_limit: "",
      is_single_use: false,  // ✅ เพิ่ม field
      start_date: "",
      end_date: "",
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code || "",
      description: code.description || "",
      percent_off: code.percent_off || "",
      amount_off: code.amount_off || "",
      max_uses: code.max_uses || "",
      per_customer_limit: code.per_customer_limit || "",
      is_single_use: code.is_single_use || false,  // ✅ เพิ่ม field
      start_date: code.start_date ? code.start_date.split("T")[0] : "",
      end_date: code.end_date ? code.end_date.split("T")[0] : "",
      is_active: code.is_active !== false,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCode(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.code.trim()) {
      addNotification("กรุณากรอกรหัสโค้ด", "warning");
      return;
    }
    if (!formData.percent_off && !formData.amount_off) {
      addNotification("กรุณากรอกส่วนลด (% หรือ ฿) อย่างน้อย 1 แบบ", "warning");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: formData.code.trim().toUpperCase(),
        description: formData.description.trim() || null,
        percent_off: formData.percent_off ? Number(formData.percent_off) : null,
        amount_off: formData.amount_off ? Number(formData.amount_off) : null,
        max_uses: formData.max_uses ? Number(formData.max_uses) : null,
        per_customer_limit: formData.per_customer_limit ? Number(formData.per_customer_limit) : null,
        is_single_use: formData.is_single_use,  // ✅ เพิ่ม field
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        is_active: formData.is_active,
      };

      if (editingCode) {
        await updateDiscountCode(editingCode.id, payload);
        addNotification("แก้ไขโค้ดส่วนลดสำเร็จ", "success");
      } else {
        await createDiscountCode(payload);
        addNotification("สร้างโค้ดส่วนลดสำเร็จ", "success");
      }

      closeModal();
      await loadCodes();
    } catch (err) {
      addNotification(
        err.response?.data?.message || "บันทึกโค้ดส่วนลดไม่สำเร็จ",
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`ต้องการลบโค้ด "${code.code}" หรือไม่?`)) return;
    try {
      await deleteDiscountCode(code.id);
      addNotification("ลบโค้ดส่วนลดสำเร็จ", "success");
      await loadCodes();
    } catch (err) {
      addNotification(
        err.response?.data?.message || "ลบโค้ดส่วนลดไม่สำเร็จ",
        "danger"
      );
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await updateDiscountCode(code.id, { is_active: !code.is_active });
      addNotification(
        `${code.is_active ? "ปิด" : "เปิด"}ใช้งานโค้ดสำเร็จ`,
        "success"
      );
      await loadCodes();
    } catch (err) {
      addNotification("เปลี่ยนสถานะไม่สำเร็จ", "danger");
    }
  };

  // Filtered codes
  const filteredCodes = codes.filter((code) => {
    const matchSearch =
      !searchTerm ||
      code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (code.description && code.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchStatus =
      !statusFilter ||
      (statusFilter === "active" && code.is_active) ||
      (statusFilter === "inactive" && !code.is_active);

    return matchSearch && matchStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("th-TH");
  };

  const getUsageCount = (code) => {
    // Backend should include usage count in response
    return code.usage_count || 0;
  };

  const isExpired = (code) => {
    if (!code.end_date) return false;
    return new Date(code.end_date) < new Date();
  };

  const isNotStarted = (code) => {
    if (!code.start_date) return false;
    return new Date(code.start_date) > new Date();
  };

  const getStatusBadge = (code) => {
    if (!code.is_active) {
      return <span className="badge bg-secondary">ปิดใช้งาน</span>;
    }
    if (isExpired(code)) {
      return <span className="badge bg-danger">หมดอายุ</span>;
    }
    if (isNotStarted(code)) {
      return <span className="badge bg-warning text-dark">ยังไม่เริ่ม</span>;
    }
    if (code.max_uses && getUsageCount(code) >= code.max_uses) {
      return <span className="badge bg-danger">ใช้ครบแล้ว</span>;
    }
    return <span className="badge bg-success">ใช้งานได้</span>;
  };

  return (
    <div className="dashboard-page">
      {/* Hero */}
      <div className="dashboard-hero mb-3">
        <div>
          <div className="dashboard-hero-title">
            <i className="bi bi-tag-fill me-2"></i>จัดการโค้ดส่วนลด
          </div>
          <div className="dashboard-hero-subtitle">
            สร้าง แก้ไข และติดตามโค้ดส่วนลดแคมเปญต่าง ๆ
          </div>
        </div>
        <button
          type="button"
          className="btn btn-ks-primary"
          style={{ color: '#ffffff' }}
          onClick={openCreateModal}
        >
          <i className="bi bi-plus-circle me-1"></i>สร้างโค้ดใหม่
        </button>
      </div>

      {/* Filters */}
      <div className="card card-ks-soft mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-center">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="ค้นหารหัสโค้ด หรือคำอธิบาย..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">ทุกสถานะ</option>
                <option value="active">ใช้งานอยู่</option>
                <option value="inactive">ปิดใช้งาน</option>
              </select>
            </div>
            <div className="col-md-3 text-end">
              <span className="badge bg-primary" style={{ fontSize: "0.9rem" }}>
                ทั้งหมด: {filteredCodes.length} โค้ด
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card card-ks-soft">
        <div className="card-body">
          {loading ? (
            <SkeletonLoader type="table" rows={5} columns={8} />
          ) : filteredCodes.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-tag fa-3x mb-3 d-block"></i>
              <p>ยังไม่มีโค้ดส่วนลดในระบบ</p>
              <button className="btn btn-sm btn-ks-primary" onClick={openCreateModal}>
                <i className="bi bi-plus-circle me-1"></i>สร้างโค้ดแรก
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "12%" }}>รหัสโค้ด</th>
                    <th style={{ width: "18%" }}>คำอธิบาย</th>
                    <th style={{ width: "10%" }} className="text-center">ส่วนลด</th>
                    <th style={{ width: "8%" }} className="text-center">ประเภท</th>
                    <th style={{ width: "12%" }} className="text-center">ใช้ไปแล้ว</th>
                    <th style={{ width: "10%" }}>วันเริ่ม</th>
                    <th style={{ width: "10%" }}>วันสิ้นสุด</th>
                    <th style={{ width: "10%" }} className="text-center">สถานะ</th>
                    <th style={{ width: "10%" }} className="text-end">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCodes.map((code) => (
                    <tr key={code.id}>
                      <td>
                        <span className="badge bg-dark" style={{ fontSize: "0.9rem" }}>
                          {code.code}
                        </span>
                      </td>
                      <td>
                        <small className="text-muted">
                          {code.description || "-"}
                        </small>
                      </td>
                      <td className="text-center">
                        <div>
                          {code.percent_off && (
                            <span className="badge text-dark me-1" style={{ backgroundColor: '#f3e5ab' }}>
                              -{code.percent_off}%
                            </span>
                          )}
                          {code.amount_off && (
                            <span className="badge bg-secondary text-white">
                              -{code.amount_off}฿
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-center">
                        {code.is_single_use ? (
                          <span className="badge bg-warning text-dark">
                            <i className="bi bi-star-fill me-1"></i>ครั้งเดียว
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <small>
                          {getUsageCount(code)}
                          {code.max_uses ? ` / ${code.max_uses}` : " / ∞"}
                        </small>
                        {code.per_customer_limit && (
                          <div>
                            <small className="text-muted">
                              (ต่อคน: {code.per_customer_limit})
                            </small>
                          </div>
                        )}
                      </td>
                      <td>
                        <small>{formatDate(code.start_date)}</small>
                      </td>
                      <td>
                        <small>{formatDate(code.end_date)}</small>
                      </td>
                      <td className="text-center">{getStatusBadge(code)}</td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary btn-ks-outline"
                            onClick={() => openEditModal(code)}
                            title="แก้ไข"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className={`btn btn-outline-${code.is_active ? "warning" : "success"} btn-ks-outline`}
                            onClick={() => handleToggleActive(code)}
                            title={code.is_active ? "ปิดใช้งาน" : "เปิดใช้งาน"}
                          >
                            <i className={`bi bi-${code.is_active ? "toggle-on" : "toggle-off"}`}></i>
                          </button>
                          <button
                            className="btn btn-outline-danger btn-ks-outline"
                            onClick={() => handleDelete(code)}
                            title="ลบ"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-tag me-2"></i>
                    {editingCode ? "แก้ไขโค้ดส่วนลด" : "สร้างโค้ดส่วนลดใหม่"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    {/* Code */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        รหัสโค้ด <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="เช่น VIP10, NEW50"
                        value={formData.code}
                        onChange={(e) =>
                          setFormData({ ...formData, code: e.target.value.toUpperCase() })
                        }
                        required
                        disabled={editingCode} // ไม่ให้แก้โค้ดหลังสร้างแล้ว
                      />
                    </div>

                    {/* Active Status */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">สถานะ</label>
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) =>
                            setFormData({ ...formData, is_active: e.target.checked })
                          }
                        />
                        <label className="form-check-label">
                          {formData.is_active ? "เปิดใช้งาน" : "ปิดใช้งาน"}
                        </label>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="col-12 mb-3">
                      <label className="form-label">คำอธิบาย</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="เช่น โปรโมชั่นลูกค้าใหม่"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({ ...formData, description: e.target.value })
                        }
                      />
                    </div>

                    {/* Percent Off */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        ส่วนลด (%) <small className="text-muted">(กรอกอย่างใดอย่างหนึ่ง หรือทั้งสอง)</small>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0-100"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.percent_off}
                        onChange={(e) =>
                          setFormData({ ...formData, percent_off: e.target.value })
                        }
                      />
                    </div>

                    {/* Amount Off */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        ส่วนลด (฿) <small className="text-muted">(กรอกอย่างใดอย่างหนึ่ง หรือทั้งสอง)</small>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="บาท"
                        min="0"
                        step="0.01"
                        value={formData.amount_off}
                        onChange={(e) =>
                          setFormData({ ...formData, amount_off: e.target.value })
                        }
                      />
                    </div>

                    {/* Max Uses */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        จำนวนครั้งใช้สูงสุด <small className="text-muted">(ว่างไว้ = ไม่จำกัด)</small>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="เช่น 100"
                        min="1"
                        value={formData.max_uses}
                        onChange={(e) =>
                          setFormData({ ...formData, max_uses: e.target.value })
                        }
                      />
                    </div>

                    {/* Per Customer Limit */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        จำกัดต่อลูกค้า <small className="text-muted">(ว่างไว้ = ไม่จำกัด)</small>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="เช่น 1, 3"
                        min="1"
                        value={formData.per_customer_limit}
                        onChange={(e) =>
                          setFormData({ ...formData, per_customer_limit: e.target.value })
                        }
                      />
                    </div>

                    {/* Single Use Toggle */}
                    <div className="col-md-12 mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.is_single_use}
                          onChange={(e) =>
                            setFormData({ ...formData, is_single_use: e.target.checked })
                          }
                        />
                        <label className="form-check-label">
                          <i className="bi bi-star-fill me-1 text-warning"></i>
                          <strong>โค้ดใช้ครั้งเดียว</strong> (หลังใช้แล้วจะถูกปิดอัตโนมัติ)
                        </label>
                      </div>
                      <small className="text-muted ms-4">
                        เหมาะสำหรับโค้ดพิเศษที่ต้องการให้ใช้ได้เพียง 1 ครั้งเดียวทั้งระบบ
                      </small>
                    </div>

                    {/* Start Date */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        วันที่เริ่มใช้งาน <small className="text-muted">(ว่างไว้ = ใช้ได้ทันที)</small>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.start_date}
                        onChange={(e) =>
                          setFormData({ ...formData, start_date: e.target.value })
                        }
                      />
                    </div>

                    {/* End Date */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        วันที่สิ้นสุด <small className="text-muted">(ว่างไว้ = ไม่มีกำหนด)</small>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.end_date}
                        onChange={(e) =>
                          setFormData({ ...formData, end_date: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="alert alert-info small mb-0">
                    <i className="bi bi-info-circle me-1"></i>
                    <strong>คำแนะนำ:</strong> โค้ดจะใช้งานได้ต่อเมื่อเปิดใช้งาน อยู่ในช่วงวันที่กำหนด
                    และไม่เกินจำนวนครั้งที่กำหนด (ทั้งรวมและต่อลูกค้า)
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    className="btn btn-ks-primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        กำลังบันทึก...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-1"></i>
                        {editingCode ? "บันทึกการแก้ไข" : "สร้างโค้ด"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountCodesPage;
